import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../contexts/AppContext';
import { isGlobalDomain } from '../../utils/constants';

/**
 * First-run onboarding + on-demand task-tour state.
 *
 * Three independent surfaces:
 *   1. Welcome modal — first login only, single OK dismiss.
 *   2. Help nav nudge — spot + tooltip on the Help sidebar item until the
 *      merchant actually visits /help.
 *   3. Task tours — invoked from the Getting-started section on the Help
 *      page ("Show me" button per task). Each tour navigates to a target
 *      page and spotlights one key element with the same blue tooltip
 *      style as the sidebar nudge. Any click dismisses.
 *
 * All three can co-exist logically but not visually — the task-tour
 * overlay is modal-ish (backdrop) so the merchant is focused on one
 * thing at a time. Retake wipes only the first-run flags; task tours
 * are on-demand and don't persist state.
 */

const WELCOME_SEEN_KEY = 'moop_welcome_seen_at';
const HELP_VISITED_KEY = 'moop_help_visited_at';
const COMPLETED_TASKS_KEY = 'moop_completed_task_tours';

// ─── DEV ONLY — resets ALL persisted onboarding state on every load ────
// The name signals the destructive half: this wipes welcome_seen, help_visited,
// and completed_task_tours on every refresh, not just forces a modal.
// Guarded by NODE_ENV so even if committed as `true` it compiles away in prod.
const DEV_RESET_ONBOARDING_ON_LOAD = process.env.NODE_ENV === 'development' && false;

export type TaskTourKey = 'explore-catalog' | 'create-ad-sales' | 'create-upsize' | 'create-offer' | 'manage-team';

export interface TaskTourStep {
  /** Route to navigate to before this step renders. Optional. If the merchant
   *  is already there, no navigation happens. */
  route?: string;
  /** CSS selector for the DOM element to highlight. TaskTourOverlay retries
   *  briefly to give the target page time to mount. */
  selector: string;
  /** Tooltip title. Optional. Body-only steps are fine for one-liners. */
  title?: string;
  /** Tooltip body. Kept to one or two sentences. */
  body: string;
  /** Tooltip placement relative to the target. Defaults to 'right'. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** CSS selector the merchant must satisfy before Next enables. Overlay
   *  polls for this element and disables the primary CTA until it exists.
   *  Used for gating on real user actions like brand selection. */
  gateSelector?: string;
  /** Copy shown next to the disabled Next button while the gate is unmet.
   *  Falls back to a generic hint if omitted. */
  gateHint?: string;
  /** When true, the step completes automatically the moment the target
   *  element leaves the DOM. Used for "click any row" style steps where
   *  the merchant's expected action navigates them off the page and the
   *  target selector stops matching. Fires the celebration and marks the
   *  task complete, same as clicking Got it on the final step. */
  completeOnDisappear?: boolean;
  /** When true, the step is skipped during chain mode (Welcome modal
   *  "Take the tour"). Used for the New Campaign / type picker steps
   *  on Ad Sales & Offer create tours — the chain already teaches
   *  that flow via the first create task in the chain (Upsize),
   *  so subsequent create tours jump straight to the wizard. */
  skipInChain?: boolean;
  /** When true, the highlighted target gently shakes to draw the
   *  merchant's eye. Used sparingly (e.g. save-as-draft back arrow). */
  shakeTarget?: boolean;
  /** When true, the highlight ring around the target is suppressed.
   *  Used for "scroll for details" style steps where boxing the target
   *  fights the intent (we want the eye moving elsewhere, not fixed on
   *  the ring). The tooltip still renders relative to the target. */
  hideRing?: boolean;
  /** When true, skip the viewport-fit auto-flip and honor `placement`
   *  literally. Useful when the step's tooltip is shorter than the
   *  global height estimate and the auto-flip would incorrectly bounce
   *  it to the opposite side. */
  disableAutoFlip?: boolean;
  /** Selector that must be ABSENT before this step is allowed to render.
   *  Needed when the previous step's expected action opens a modal or drawer:
   *  its mask makes the page underneath unclickable, so a tooltip pointing
   *  there would instruct the merchant to do something they physically
   *  can't. The overlay stays invisible until this clears, without spending
   *  its target-retry budget. Matched on visibility, not mere presence —
   *  antd keeps closed modals mounted. */
  blockedBySelector?: string;
  /** Route to use instead of `route` while the tour is running on sample
   *  data. A merchant with no campaigns has no real detail page to open, so
   *  those steps run on the read-only /help/tour replica instead. */
  sampleRoute?: string;
  /** When true, an animated mouse cursor pulses over the target's centre
   *  to signal "you click this next." Use on click-expected steps where
   *  the target isn't self-evidently interactive (a table row, a subtle
   *  chip) — over-using it on obvious buttons is noise. */
  showClickCursor?: boolean;
  /** Optional separate selector for the cursor position. When set (and
   *  `showClickCursor` is true), the tooltip anchors on `selector` but
   *  the cursor pulses on THIS element instead. Useful when the tooltip
   *  explains a read-only area but the actual click is on a different
   *  control (e.g. "Look at this content" + cursor on the Next button). */
  cursorSelector?: string;
}

/**
 * Task tour registry. One entry per Getting-started task. Each tour is
 * a walkthrough of the primary elements on its destination page. Kept
 * to what's visible on initial page load so no wizard driving is
 * required. Merchants do the actual work themselves at their own pace.
 */
export const TASK_TOURS: Record<TaskTourKey, TaskTourStep[]> = {
  'explore-catalog': [
    {
      // The catalog switches views with a Segmented control, not antd Tabs, so there is no
      // `.ant-tabs-nav-list` to spotlight; the overlay gave up after its retry window and the
      // tour ended on step 1. Target the wrapper, which both the real page and the /help/tour
      // replica render. The route carries the tab because step 2 (the filter row) only exists on
      // All campaigns.
      route: '/campaigns?tab=all-campaigns',
      selector: '[data-tour-target="catalog-tabs"]',
      title: 'Two tabs',
      body: 'All campaigns shows every booking. Pending review is where ShopBack edits wait for your OK.',
      placement: 'right',
    },
    {
      selector: '[data-tour-target="catalog-filters"]',
      title: 'Search and filter',
      body: 'Narrow the list by brand, campaign type, status, or date range. Search matches name and ID.',
      placement: 'top',
      disableAutoFlip: true,
    },
    {
      // The catalog lists the merchant's own campaigns, so no id can be
      // hardcoded here — spotlight whichever row happens to be first.
      //
      // Deliberately no `:first-child`. CatalogTable passes `scroll={{ x }}`,
      // which makes rc-table emit an aria-hidden `ant-table-measure-row` as
      // tbody's first child — so `tr.ant-table-row:first-child` matches
      // nothing. querySelector already returns the first match in document
      // order, which is the first real data row.
      selector: '.ant-table-tbody > tr.ant-table-row',
      title: 'Open a campaign',
      body: 'Each row is one booking. Click it to open the detail page.',
      placement: 'bottom',
      completeOnDisappear: true,
      // Table rows don't scream "clickable" — the pulsing cursor over the
      // row makes the interaction explicit.
      showClickCursor: true,
    },
    {
      // First step on the detail page. The route is the catalog prefix, not
      // a concrete id — /campaigns/:id starts with it, so the merchant's own
      // row click satisfies the previous step's advance check and this step
      // needs no navigation of its own. There is no id to navigate TO: the
      // campaign is whichever one they opened.
      route: '/campaigns',
      // No real campaign exists to open when the catalog is on samples, so
      // the detail half of the tour runs on the read-only replica.
      sampleRoute: '/help/tour/campaigns',
      selector: '[data-tour-target="detail-campaign-info"]',
      title: 'Review the details',
      body: 'Package, tiers, and dates — everything you need before deciding.',
      // Above, not beside: the details block spans the full content width, so
      // a side placement has nowhere to sit and gets flipped over the content
      // it's describing.
      placement: 'top',
      hideRing: true,
    },
    {
      // Target just the buttons group, not the full sticky footer bar.
      // Placement 'top' puts the tooltip directly above the buttons —
      // points down at them, reads as a callout for the primary action.
      // Copy stays status-agnostic on purpose: the merchant opens one of
      // their own campaigns, so what sits here depends on its status
      // (Approve / Reject while a decision is pending, Resubmit after a
      // rejection, Duplicate once it's terminal).
      selector: '[data-tour-target="detail-actions-buttons"]',
      title: 'Act on the campaign',
      body: "Whatever this campaign needs next happens here — approving ShopBack's edits, sending them back, or resubmitting.",
      placement: 'top',
    },
  ],
  'create-upsize': [
    {
      route: '/campaigns',
      selector: '[data-tour-target="new-campaign-btn"]',
      gateSelector: '[data-tour-target="type-picker-upsize"]',
      title: 'Start a new campaign',
      body: 'Every booking begins here. Click New Campaign to see the three types you can book.',
      placement: 'bottom',
      showClickCursor: true,
    },
    {
      selector: '[data-tour-target="type-picker-upsize"]',
      gateSelector: '[data-tour-target="wizard-brand"]',
      title: 'Choose Upsize',
      body: 'Upsize temporarily boosts your cashback rate for selected categories. Click the card to start.',
      placement: 'right',
    },
    {
      route: '/create/upsize',
      selector: '[data-tour-target="wizard-brand"]',
      gateSelector: '[data-tour-target="wizard-brand"] .ant-select-selection-item',
      gateHint: 'Select a brand to continue',
      title: 'Pick a brand',
      body: 'Your Exposure Package options and category baselines depend on the brand you choose here.',
      placement: 'right',
    },
    {
      selector: '[data-tour-target="upsize-package"]',
      // ExposurePackagePicker is a custom (non-antd) picker. Empty state
      // renders `<span>Select a package...</span>`; selected state renders
      // `<strong>{name}</strong>` inside the trigger. Gate on the `<strong>`
      // — it's absent until a package is chosen and dropdown options use
      // `<span>` (not `<strong>`), so opening the popover doesn't false-fire.
      gateSelector: '[data-tour-target="upsize-package"] strong',
      gateHint: 'Choose a package to continue',
      title: 'Pick your Exposure Package',
      body: 'Choose the package first. It sets the boost ceiling and which categories you can upsize.',
      placement: 'right',
    },
    {
      selector: '[data-tour-target="upsize-dates"]',
      // Gate on the END date being filled — Start is often prefilled, so
      // only End being non-empty signals "the merchant actually picked a
      // range." antd v4 DatePicker uses a native placeholder attribute on
      // its inner input; `:not(:placeholder-shown)` fires once a value
      // exists.
      gateSelector: '[data-tour-target="upsize-dates"] input[placeholder="End date"]:not(:placeholder-shown)',
      gateHint: 'Set the end date to continue',
      title: 'Set date and time',
      body: 'Pick the campaign window. The range decides which categories are eligible and how high you can lift their rates.',
      placement: 'right',
    },
    {
      selector: '[data-tour-target="upsize-rates"]',
      title: 'Adjust category rates',
      // No gate: rates auto-populate at baseline once package + dates are
      // set, so any value-based gate would fire immediately and the step
      // would flash by. Merchant reads the tip and clicks Next.
      body: 'Every category starts at its baseline. Bump any of them up to boost cashback for this window — this step is optional.',
      placement: 'right',
    },
    {
      selector: '[data-tour-target="upsize-add-another"]',
      title: 'Add another Upsize',
      body: 'Book multiple Upsizes in one flow. Each one is reviewed and tracked as a separate campaign.',
      placement: 'left',
    },
    {
      // Anchor on the footer button this step is actually about. Anchoring
      // on page content instead put the tooltip at the top of the scroll
      // area — a full viewport away from the button it describes.
      selector: '[data-tour-target="wizard-footer"] .ant-btn-primary',
      gateSelector: '[data-tour-target="upsize-review"]',
      title: 'Continue to Review',
      body: 'When your Upsizes look right, click Continue to Review to check everything and submit.',
      placement: 'top',
      showClickCursor: true,
    },
    {
      selector: '[data-tour-target="wizard-back"]',
      title: 'Save as draft anytime',
      body: 'Click the back arrow to save your progress as a draft. You can save at any time.',
      placement: 'bottom',
      shakeTarget: true,
      // Task completes when the wizard unmounts (merchant chose Save
      // or Discard in the confirmation modal). Clicking Got it also
      // completes normally.
      completeOnDisappear: true,
    },
  ],
  'create-ad-sales': [
    {
      route: '/campaigns',
      selector: '[data-tour-target="new-campaign-btn"]',
      gateSelector: '[data-tour-target="type-picker-ad-sales"]',
      title: 'Start a new campaign',
      body: 'Every booking begins here. Click New Campaign to see the three types you can book.',
      placement: 'bottom',
      showClickCursor: true,
    },
    {
      selector: '[data-tour-target="type-picker-ad-sales"]',
      gateSelector: '[data-tour-target="wizard-brand"]',
      title: 'Choose Ad Sales',
      body: 'Ad Sales buys featured placements across ShopBack. Click the card to start booking one.',
      placement: 'right',
    },
    {
      route: '/create/ad-sales',
      selector: '[data-tour-target="wizard-brand"]',
      gateSelector: '[data-tour-target="wizard-brand"] .ant-select-selection-item',
      gateHint: 'Select a brand to continue',
      title: 'Pick a brand',
      body: 'Every booking is scoped to one brand. Your available packages and invoicing details follow this choice.',
      placement: 'right',
    },
    {
      // Anchor on the inner packages container (not the whole setup
      // section) so the tooltip sits alongside the package list, not
      // way above at the section header.
      selector: '[data-tour-target="ad-sales-packages"]',
      // Gate on the "In your selection" block appearing — it only
      // renders inside a package card once the merchant clicks "Add to
      // plan", which is a reliable "at least one package picked" signal.
      gateSelector: '[data-tour-target="ad-sales-package-selected"]',
      gateHint: 'Add at least one package to continue',
      title: 'Choose packages',
      body: 'Pick one or more packages and set start and end dates for each. Prices are fixed per package.',
      placement: 'right',
    },
    {
      // Anchor on the footer button this step is actually about — see the
      // matching step in create-upsize for why page content is the wrong
      // anchor here.
      selector: '[data-tour-target="wizard-footer"] .ant-btn-primary',
      gateSelector: '[data-tour-target="ad-sales-invoicing"]',
      title: 'Continue to invoicing',
      body: 'When your plan looks right, click Next to review invoicing details.',
      placement: 'top',
      showClickCursor: true,
    },
    {
      selector: '[data-tour-target="ad-sales-invoicing"]',
      gateSelector: '[data-tour-target="ad-sales-review"]',
      title: 'Invoicing details',
      body: "These come from your brand's saved defaults — legal entity, GST, bank account and finance contact. Take a look, then click Next.",
      placement: 'top',
      // Cursor pulses on the wizard footer's Next button while the
      // tooltip stays anchored on the invoicing content — makes the
      // "click Next" instruction from the copy visually explicit.
      showClickCursor: true,
      cursorSelector: '[data-tour-target="wizard-footer"] .ant-btn-primary',
    },
    {
      selector: '[data-tour-target="ad-sales-review"]',
      gateSelector: '[data-tour-target="ad-sales-sign-io"]',
      title: 'Review before submitting',
      body: 'Look over the booking and invoicing. Tick the Terms and Conditions checkbox at the bottom, then click Next to continue.',
      placement: 'right',
    },
    {
      selector: '[data-tour-target="ad-sales-sign-io"]',
      title: 'Sign the IO',
      body: 'Download the IO, sign it, and upload the signed copy here. Submit sends everything to ShopBack for countersign.',
      placement: 'top',
    },
    {
      selector: '[data-tour-target="wizard-back"]',
      title: 'Save as draft anytime',
      body: 'Click the back arrow to save your progress as a draft. You can save at any time.',
      placement: 'bottom',
      shakeTarget: true,
      // Task completes when the wizard unmounts (merchant chose Save
      // or Discard in the confirmation modal). Clicking Got it also
      // completes normally.
      completeOnDisappear: true,
    },
  ],
  'create-offer': [
    {
      route: '/campaigns',
      selector: '[data-tour-target="new-campaign-btn"]',
      gateSelector: '[data-tour-target="type-picker-standalone-offer"]',
      title: 'Start a new campaign',
      body: 'Every booking begins here. Click New Campaign to see the three types you can book.',
      placement: 'bottom',
      showClickCursor: true,
    },
    {
      selector: '[data-tour-target="type-picker-standalone-offer"]',
      gateSelector: '[data-tour-target="wizard-brand"]',
      title: 'Choose Standalone Offer',
      body: 'Standalone Offer runs a shopper-facing deal like % off or free shipping. Click the card to start.',
      placement: 'right',
    },
    {
      route: '/create/standalone-offer',
      selector: '[data-tour-target="wizard-brand"]',
      gateSelector: '[data-tour-target="wizard-brand"] .ant-select-selection-item',
      gateHint: 'Select a brand to continue',
      title: 'Pick a brand',
      body: 'Every offer is scoped to one brand. Pick the one this deal is for.',
      placement: 'right',
    },
    {
      // Anchor on the Offer details SectionCard specifically (not the
      // full scrollable wizard-content) so placement is deterministic —
      // otherwise the tall content area centres the tooltip mid-viewport
      // and it can drift below the fold.
      selector: '[data-tour-target="offer-details"]',
      title: 'Compose the deal',
      body: 'Title, description, hero image, landing URL, and start and end dates. Optionally link to an Upsize or Ad Sales campaign for cross promotion.',
      placement: 'right',
    },
    {
      // Anchor on the footer button this step is actually about — see the
      // matching step in create-upsize for why page content is the wrong
      // anchor here.
      selector: '[data-tour-target="wizard-footer"] .ant-btn-primary',
      gateSelector: '[data-tour-target="offer-submitted"]',
      title: 'Submit for Review',
      body: "Click Submit for Review to send it to ShopBack. They'll review and get the offer live on your start date.",
      placement: 'top',
      showClickCursor: true,
    },
  ],
  'manage-team': [
    {
      // Anchor on the brand matrix itself (not the search input) so the
      // tooltip sits by the actual content the merchant is learning
      // about. Manage-team is admin-only per gettingStarted.ts, so
      // non-admins never reach this tour to begin with.
      route: '/admin',
      selector: '[data-tour-target="brand-matrix"]',
      title: 'Your team',
      body: 'Everyone on your workspace lives here. Each row is a brand. Admins have full control, Members have standard access. Use the search on the top right to find a specific teammate.',
      placement: 'top',
    },
    {
      // First "+ Add" button in the matrix. Cursor + gate on the antd
      // Modal appearing (InviteModal opens on click) so the tour advances
      // the moment the merchant tries the action.
      selector: '[data-tour-target="team-add-button"]',
      gateSelector: '.ant-modal-body',
      title: 'Add someone to a brand',
      body: 'Click Add on any brand row to give an existing teammate access to that brand.',
      placement: 'left',
      showClickCursor: true,
    },
    {
      // First non-self member chip. Cursor + gate on the drawer
      // appearing — clicking a chip opens the per-brand permissions
      // drawer. UserChip omits the tour target on the current user's
      // own chip, so this selector always lands on a real teammate.
      selector: '[data-tour-target="team-member-chip"]',
      // The previous step advances the moment the Add modal opens, which is
      // the right signal but leaves the modal up. Hold this step back until
      // the merchant has dismissed it — the chip is behind the modal mask
      // until then.
      blockedBySelector: '.ant-modal-body',
      gateSelector: '.ant-drawer-body',
      title: 'Edit access',
      body: "Click any teammate's chip to open their drawer. From there you can swap between Admin and Member, or remove them from a brand.",
      placement: 'bottom',
      showClickCursor: true,
    },
  ],
};

interface OnboardingTourContextValue {
  welcomeVisible: boolean;
  helpNudgeVisible: boolean;
  /** Task tour currently running (null when idle). */
  activeTaskTour: TaskTourKey | null;
  /** Which step of the active tour is showing. 0-indexed. */
  taskTourStepIndex: number;
  /** When true, the overlay chains from one task tour to the next
   *  automatically after each celebration. Set by "Take the tour" on
   *  the welcome modal so merchants get a guided walkthrough of every
   *  starter task in one flow. Cleared when the merchant closes early
   *  or when the queue drains. */
  chainMode: boolean;
  setChainMode: (on: boolean) => void;
  /** True while a tour is running against sample campaigns because the
   *  merchant hasn't booked anything yet. Set by Campaign manager, read by
   *  the overlay so steps can resolve their `sampleRoute`. */
  sampleTourData: boolean;
  setSampleTourData: (on: boolean) => void;
  /** Which task tours the merchant has completed at least once. Persisted
   *  across sessions in localStorage (cleared in DEV mode on every refresh). */
  completedTasks: Set<TaskTourKey>;
  dismissWelcome: () => void;
  markVisitedHelp: () => void;
  retake: () => void;
  /** Start a task tour from Getting-started. Overlay handles the rest. */
  startTaskTour: (key: TaskTourKey) => void;
  /** Advance to the next step, or end the tour if we're on the last one. */
  nextTaskTourStep: () => void;
  /** Dismiss the tour from anywhere (click on backdrop, ESC, close button). */
  endTaskTour: () => void;
  /** Mark a task tour as complete. Called by the overlay after the merchant
   *  clicks Got it on the final step and the celebration finishes. */
  markTaskComplete: (key: TaskTourKey) => void;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

const readFlag = (key: string): boolean => {
  try {
    return Boolean(window.localStorage.getItem(key));
  } catch {
    return false;
  }
};

const writeFlag = (key: string): void => {
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // localStorage can throw in private-mode Safari etc. Fail safe.
  }
};

const clearFlag = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // No-op.
  }
};

const readCompletedTasks = (): Set<TaskTourKey> => {
  try {
    const raw = window.localStorage.getItem(COMPLETED_TASKS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as TaskTourKey[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const writeCompletedTasks = (tasks: Set<TaskTourKey>): void => {
  try {
    window.localStorage.setItem(COMPLETED_TASKS_KEY, JSON.stringify(Array.from(tasks)));
  } catch {
    // No-op.
  }
};

export const OnboardingTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useContext(AppContext);
  const [welcomeVisible, setWelcomeVisible] = useState(false);
  const [helpNudgeVisible, setHelpNudgeVisible] = useState(false);
  const [activeTaskTour, setActiveTaskTour] = useState<TaskTourKey | null>(null);
  const [taskTourStepIndex, setTaskTourStepIndex] = useState(0);
  const [completedTasks, setCompletedTasks] = useState<Set<TaskTourKey>>(new Set());
  const [chainMode, setChainMode] = useState(false);
  const [sampleTourData, setSampleTourData] = useState(false);

  // Wait for an authenticated MC MOOP user before showing onboarding.
  // user is IUser (object) when authenticated, false/undefined otherwise.
  // isGlobalDomain() gates on the portal hostname (merchant portal only).
  const isAuthenticatedMoopUser = typeof user === 'object' && user !== null && isGlobalDomain();

  useEffect(() => {
    if (!isAuthenticatedMoopUser) return;
    if (DEV_RESET_ONBOARDING_ON_LOAD) {
      // Full first-run reset: wipe every persisted flag so refresh always
      // shows the welcome modal, Help nav nudge, and non-completed tasks.
      clearFlag(WELCOME_SEEN_KEY);
      clearFlag(HELP_VISITED_KEY);
      clearFlag(COMPLETED_TASKS_KEY);
      setWelcomeVisible(true);
      setHelpNudgeVisible(false);
      setCompletedTasks(new Set());
      return;
    }
    const welcomeSeen = readFlag(WELCOME_SEEN_KEY);
    const helpVisited = readFlag(HELP_VISITED_KEY);
    if (!welcomeSeen) {
      setWelcomeVisible(true);
    } else if (!helpVisited) {
      setHelpNudgeVisible(true);
    }
    setCompletedTasks(readCompletedTasks());
  }, [isAuthenticatedMoopUser]);

  const dismissWelcome = useCallback(() => {
    writeFlag(WELCOME_SEEN_KEY);
    setWelcomeVisible(false);
    if (!readFlag(HELP_VISITED_KEY)) {
      setHelpNudgeVisible(true);
    }
  }, []);

  const markVisitedHelp = useCallback(() => {
    writeFlag(HELP_VISITED_KEY);
    setHelpNudgeVisible(false);
  }, []);

  const retake = useCallback(() => {
    clearFlag(WELCOME_SEEN_KEY);
    clearFlag(HELP_VISITED_KEY);
    clearFlag(COMPLETED_TASKS_KEY);
    setWelcomeVisible(true);
    setHelpNudgeVisible(false);
    setActiveTaskTour(null);
    setTaskTourStepIndex(0);
    setCompletedTasks(new Set());
    setChainMode(false);
  }, []);

  const markTaskComplete = useCallback((key: TaskTourKey) => {
    setCompletedTasks((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      writeCompletedTasks(next);
      return next;
    });
  }, []);

  const startTaskTour = useCallback((key: TaskTourKey) => {
    setActiveTaskTour(key);
    setTaskTourStepIndex(0);
  }, []);

  const endTaskTour = useCallback(() => {
    setActiveTaskTour(null);
    setTaskTourStepIndex(0);
    setChainMode(false);
    // Sample mode is asserted by Campaign manager per tour; clear it here so
    // a later tour can't inherit a stale "merchant has no campaigns" verdict.
    setSampleTourData(false);
  }, [chainMode]);

  const nextTaskTourStep = useCallback(() => {
    if (!activeTaskTour) return;
    const steps = TASK_TOURS[activeTaskTour];
    const nextIndex = taskTourStepIndex + 1;
    if (nextIndex >= steps.length) {
      setActiveTaskTour(null);
      setTaskTourStepIndex(0);
      setSampleTourData(false);
    } else {
      setTaskTourStepIndex(nextIndex);
    }
  }, [activeTaskTour, taskTourStepIndex]);

  const value = useMemo<OnboardingTourContextValue>(
    () => ({
      welcomeVisible,
      helpNudgeVisible,
      activeTaskTour,
      taskTourStepIndex,
      completedTasks,
      chainMode,
      setChainMode,
      sampleTourData,
      setSampleTourData,
      dismissWelcome,
      markVisitedHelp,
      retake,
      startTaskTour,
      nextTaskTourStep,
      endTaskTour,
      markTaskComplete,
    }),
    [
      welcomeVisible,
      helpNudgeVisible,
      activeTaskTour,
      taskTourStepIndex,
      completedTasks,
      chainMode,
      sampleTourData,
      dismissWelcome,
      markVisitedHelp,
      retake,
      startTaskTour,
      nextTaskTourStep,
      endTaskTour,
      markTaskComplete,
    ]
  );

  return <OnboardingTourContext.Provider value={value}>{children}</OnboardingTourContext.Provider>;
};

export const useOnboardingTour = (): OnboardingTourContextValue => {
  const ctx = useContext(OnboardingTourContext);
  if (!ctx) {
    throw new Error('useOnboardingTour must be used inside <OnboardingTourProvider>');
  }
  return ctx;
};
